import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Flame, PackageSearch, Scale, Trophy, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import type { Weighing } from '@container-system/shared';
import { statsApi } from '../../api/stats';
import { listWeighings } from '../../api/weighings';
import { useSocketEvent } from '../../app/socket';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatDateTime, formatWeight } from '../../utils/format';

const WEEKDAY_LABELS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

type Tone = 'accent' | 'violet' | 'success' | 'warning' | 'danger';

const TONE_CLASSES: Record<Tone, string> = {
  accent: 'bg-accent-500/15 text-accent-300',
  violet: 'bg-violet-500/15 text-violet-300',
  success: 'bg-success-500/15 text-success-400',
  warning: 'bg-warning-500/15 text-warning-400',
  danger: 'bg-danger-500/15 text-danger-400',
};

/** Farbige Kennzahl-Kachel mit Icon - Persönlichkeit statt einer reihenweise identischen grauen Statistik-Karte. */
function KpiTile({ icon: Icon, label, value, sub, tone = 'accent' }: { icon: LucideIcon; label: string; value: string; sub?: string; tone?: Tone }) {
  return (
    <div className="rounded-lg border border-surface-600 bg-surface-800 px-4 py-3.5 shadow-card">
      <div className="flex items-center gap-2.5">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${TONE_CLASSES[tone]}`}>
          <Icon className="h-4.5 w-4.5" strokeWidth={1.85} />
        </span>
        <p className="truncate text-xs font-medium uppercase tracking-wide text-ivory-500">{label}</p>
      </div>
      <p className="mt-2 truncate text-2xl font-bold text-ivory-100">{value}</p>
      {sub && <p className="mt-0.5 truncate text-xs text-ivory-500">{sub}</p>}
    </div>
  );
}

export function DashboardPage() {
  const queryClient = useQueryClient();

  const dashboardQuery = useQuery({ queryKey: ['stats', 'dashboard'], queryFn: statsApi.dashboard, refetchInterval: 60_000 });
  const peakQuery = useQuery({ queryKey: ['stats', 'peak'], queryFn: statsApi.peak, staleTime: 5 * 60_000 });
  const recentQuery = useQuery({
    queryKey: ['weighings', 'recent'],
    queryFn: () => listWeighings({ page: 1, pageSize: 15 }),
  });

  useSocketEvent<Weighing>('weighing:created', () => {
    void queryClient.invalidateQueries({ queryKey: ['stats', 'dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['weighings', 'recent'] });
  });

  if (dashboardQuery.isLoading || recentQuery.isLoading) return <FullPageSpinner />;
  if (dashboardQuery.isError || !dashboardQuery.data) return <p className="text-sm text-danger-400">Dashboard konnte nicht geladen werden.</p>;

  const stats = dashboardQuery.data;
  const weightDeltaToday = stats.today.totalWeightKg - stats.yesterdayComparison.totalWeightKg;
  const deltaSign = weightDeltaToday >= 0 ? '+' : '';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Dashboard" description="Live-Kennzahlen, automatisch aktualisiert." />

      {/* Status-Leiste - spiegelt den tatsächlichen Live-Zustand (Socket.IO-Push + 60s-Poll), keine dekorative Attrappe. */}
      <div className="flex items-center justify-between rounded-lg border border-success-500/30 bg-success-500/10 px-4 py-2.5">
        <span className="text-sm font-medium text-ivory-200">Live-Erfassung</span>
        <span className="flex items-center gap-2 text-sm font-semibold text-success-400">
          <span className="relative flex h-2 w-2">
            <span className="absolute h-full w-full animate-pulse-glow rounded-full bg-success-400" />
          </span>
          Online
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile icon={Scale} label="Heute" value={`${stats.today.count}`} sub={formatWeight(stats.today.totalWeightKg)} tone="accent" />
        <KpiTile icon={Scale} label="Diese Woche" value={`${stats.week.count}`} sub={formatWeight(stats.week.totalWeightKg)} tone="violet" />
        <KpiTile icon={Scale} label="Dieser Monat" value={`${stats.month.count}`} sub={formatWeight(stats.month.totalWeightKg)} tone="accent" />
        <KpiTile
          icon={weightDeltaToday >= 0 ? TrendingUp : TrendingDown}
          label="Ggü. Gestern"
          value={`${deltaSign}${formatWeight(weightDeltaToday)}`}
          sub={`Gestern: ${stats.yesterdayComparison.count} Wiegungen`}
          tone={weightDeltaToday >= 0 ? 'success' : 'danger'}
        />
        <KpiTile icon={Clock} label="Letzte Stunde" value={`${stats.currentHourCount}`} sub="Wiegungen" tone="warning" />
        <KpiTile icon={Flame} label="Serie" value={`${stats.streakDays} Tage`} sub="mit mind. 1 Wiegung" tone="warning" />
        <KpiTile icon={Trophy} label="Rekordtag" value={stats.recordDay ? `${stats.recordDay.count}` : '—'} sub={stats.recordDay?.date} tone="violet" />
        <KpiTile
          icon={Calendar}
          label="Ø Gewicht / Tag (30d)"
          value={peakQuery.data?.averageWeightPerDayKg != null ? formatWeight(peakQuery.data.averageWeightPerDayKg) : '—'}
          sub={peakQuery.data?.peakHour != null ? `Stoßzeit: ${peakQuery.data.peakHour}:00 Uhr` : undefined}
          tone="accent"
        />
      </div>

      {peakQuery.data?.topContainer && (
        <p className="flex items-center gap-1.5 text-sm text-ivory-500">
          <PackageSearch className="h-4 w-4 shrink-0 text-ivory-500" strokeWidth={1.75} />
          Meistgenutzter Container (30 Tage): <span className="font-mono text-ivory-200">{peakQuery.data.topContainer.name}</span> (
          {peakQuery.data.topContainer.count}×)
          {peakQuery.data.peakWeekdayIndex != null && <> · Stärkster Wochentag: {WEEKDAY_LABELS[peakQuery.data.peakWeekdayIndex]}</>}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Letzte Aktivitäten</CardTitle>
          <Link to="/weighings" className="text-sm font-medium text-accent-400 hover:underline">
            Alle anzeigen →
          </Link>
        </CardHeader>
        <CardBody className="p-0">
          {recentQuery.data?.items.length === 0 ? (
            <div className="p-5">
              <EmptyState title="Noch keine Wiegungen erfasst." />
            </div>
          ) : (
            <ul className="divide-y divide-surface-700">
              {recentQuery.data?.items.map((w) => (
                <li key={w.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm transition-colors hover:bg-accent-500/5">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ivory-100">
                      {w.container.name} · {w.material.name}
                      {w.alloy && <span className="text-ivory-500"> ({w.alloy.name})</span>}
                    </p>
                    <p className="font-mono text-xs text-ivory-500">
                      {formatDateTime(w.dateTime)} · {w.plant.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {w.source === 'OCR' && <Badge tone="violet">OCR</Badge>}
                    <span className="font-mono font-semibold text-ivory-100">{formatWeight(w.netWeightKg)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
